/**
 * Dialog for adding a new stock class to a feed budget animal group.
 * Collects type-specific inputs and computes per-month allocation curves.
 */

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { endOfMonth, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import {
  STOCK_CLASS_TYPES,
  StockClassTypeId,
  cattleDlwgToAllocation,
  computeAnimalCountsByMonth,
  computeBreedingCowAllocations,
  computeBreedingEweAllocations,
  computeDairyCowAllocations,
  computeSpawnedAnimalCounts,
  lambDlwgToAllocation,
} from "./stockClassAllocationUtils";

export interface StockClassInput {
  name: string;
  defaultStockClassName: string;
  initialValues: {
    animalCount: number;
    weightKg: number;
    allocationByMonth?: number[];
    animalCountByMonth?: number[];
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with one or two stock classes to add (breeding types spawn a growing class). */
  onAdd: (classes: StockClassInput[]) => void;
  /** ISO date string for the plan start (e.g. "2025-07-01"). Used to correctly assign
   *  animal counts to months for cross-year plans. */
  planStartDate?: string;
  /** ISO date string for the plan end. Used to warn when breeding dates fall outside
   *  the plan period. */
  planEndDate?: string;
  /** Called when the farmer wants to extend the plan to cover the weaning date. */
  onExtendPlan?: (newEndDate: string) => void;
}

const DEFAULT_TYPE: StockClassTypeId = "DAIRY_COW";

export default function AddStockClassDialog({
  open,
  onClose,
  onAdd,
  planStartDate,
  planEndDate,
  onExtendPlan,
}: Props) {
  const planStartDateObj = planStartDate ? new Date(planStartDate) : null;
  const planEndDateObj = planEndDate ? new Date(planEndDate) : null;
  const [type, setType] = useState<StockClassTypeId>(DEFAULT_TYPE);
  const [name, setName] = useState("");
  const [animalCount, setAnimalCount] = useState(0);
  const [weightKg, setWeightKg] = useState(550);

  // Dairy cow
  const [dairyCowSystem, setDairyCowSystem] = useState<"block" | "ayr">(
    "block"
  );
  const [calvingDate, setCalvingDate] = useState<Date | null>(null);
  const [dryOffDate, setDryOffDate] = useState<Date | null>(null);
  const [yieldLDay, setYieldLDay] = useState(25);
  // AYR dairy
  const [lactatingCount, setLactatingCount] = useState(0);
  const [dryCount, setDryCount] = useState(0);
  const [ayrYieldLDay, setAyrYieldLDay] = useState(25);

  // Replacement heifer / growing cattle
  const [dlwgKgDay, setDlwgKgDay] = useState(0.5);

  // Breeding cow
  // "mating": enter mating date → calving = mating + 283d → weaning = calving + 180d
  // "calving": enter calving date → weaning = calving + 180d
  const [breedingCowStage, setBreedingCowStage] = useState<
    "mating" | "calving"
  >("calving");
  const [matingDate, setMatingDate] = useState<Date | null>(null);
  const [breedingCalvingDate, setBreedingCalvingDate] = useState<Date | null>(
    null
  );
  const effectiveCalvingDate =
    breedingCowStage === "mating" && matingDate
      ? new Date(matingDate.getTime() + 283 * 24 * 60 * 60 * 1000)
      : breedingCalvingDate;
  const weaningDate = effectiveCalvingDate
    ? new Date(effectiveCalvingDate.getTime() + 180 * 24 * 60 * 60 * 1000)
    : null;
  const [spawnedCount, setSpawnedCount] = useState(0);
  const [spawnedWeightKg, setSpawnedWeightKg] = useState(200);

  // Breeding ewe
  // "tupping": enter tupping date → lambing = tupping + 147d → weaning = lambing + 90d
  // "lambing": enter lambing date → weaning = lambing + 90d (tupping back-derived)
  const [breedingEweStage, setBreedingEweStage] = useState<
    "tupping" | "lambing"
  >("tupping");
  const [tuppingDate, setTuppingDate] = useState<Date | null>(null);
  const [lambingDateDirect, setLambingDateDirect] = useState<Date | null>(null);
  const effectiveLambingDate =
    breedingEweStage === "lambing"
      ? lambingDateDirect
      : tuppingDate
      ? new Date(tuppingDate.getTime() + 147 * 24 * 60 * 60 * 1000)
      : null;
  // Back-derive tupping from lambing for allocation computation
  const effectiveTuppingDate =
    breedingEweStage === "lambing" && lambingDateDirect
      ? new Date(lambingDateDirect.getTime() - 147 * 24 * 60 * 60 * 1000)
      : tuppingDate;
  const ewWeaningDate = effectiveLambingDate
    ? new Date(effectiveLambingDate.getTime() + 90 * 24 * 60 * 60 * 1000)
    : null;
  const [scanningPct, setScanningPct] = useState(140);
  const [spawnedLambCount, setSpawnedLambCount] = useState(0);
  const [spawnedLambWeightKg, setSpawnedLambWeightKg] = useState(35);

  // Growing lamb
  const [dlwgGDay, setDlwgGDay] = useState(100);

  // Sale date (growing cattle / growing lamb)
  const [saleDate, setSaleDate] = useState<Date | null>(null);

  // Bull / ram allocation level
  const [allocationLevel, setAllocationLevel] = useState<
    "maintenance" | "active"
  >("active");

  // STOCK_CLASS_TYPES always contains an entry for each valid type
  const typeConfig =
    STOCK_CLASS_TYPES.find((t) => t.id === type) ?? STOCK_CLASS_TYPES[0];

  // Reset defaults when type changes
  useEffect(() => {
    setName(typeConfig.label);
    setWeightKg(typeConfig.defaultWeightKg);
    setAnimalCount(0);
    setDairyCowSystem("block");
    setCalvingDate(null);
    setDryOffDate(null);
    setYieldLDay(25);
    setLactatingCount(0);
    setDryCount(0);
    setAyrYieldLDay(25);
    setDlwgKgDay(0.5);
    setBreedingCowStage("calving");
    setMatingDate(null);
    setBreedingCalvingDate(null);
    setSpawnedCount(0);
    setSpawnedWeightKg(80);
    setBreedingEweStage("tupping");
    setTuppingDate(null);
    setLambingDateDirect(null);
    setScanningPct(140);
    setSpawnedLambCount(0);
    setSpawnedLambWeightKg(35);
    setDlwgGDay(100);
    setSaleDate(null);
    setAllocationLevel("active");
  }, [type]); // type change resets all dependent state; setter functions are stable

  // Auto-estimate spawned class counts from parent herd size
  useEffect(() => {
    if (type === "BREEDING_COW") setSpawnedCount(animalCount);
  }, [type, animalCount]);

  useEffect(() => {
    if (type === "DAIRY_COW" && dairyCowSystem === "ayr") {
      setLactatingCount(Math.round(animalCount * 0.85));
      setDryCount(animalCount - Math.round(animalCount * 0.85));
    }
  }, [type, dairyCowSystem, animalCount]);

  useEffect(() => {
    if (type === "BREEDING_EWE")
      setSpawnedLambCount(Math.round((animalCount * scanningPct) / 100));
  }, [type, animalCount, scanningPct]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setType(DEFAULT_TYPE);
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    const classes: StockClassInput[] = [];

    if (type === "DAIRY_COW") {
      if (dairyCowSystem === "block") {
        const allocationByMonth =
          calvingDate && dryOffDate
            ? computeDairyCowAllocations(
                calvingDate,
                dryOffDate,
                yieldLDay,
                weightKg
              )
            : undefined;
        classes.push({
          name: name || typeConfig.label,
          defaultStockClassName: "DAIRY_COW",
          initialValues: { animalCount, weightKg, allocationByMonth },
        });
      } else {
        const lactatingPct = parseFloat(
          (((weightKg * 0.02 + ayrYieldLDay * 0.45) / weightKg) * 100).toFixed(
            2
          )
        );
        classes.push({
          name: "Lactating Cows",
          defaultStockClassName: "DAIRY_COW",
          initialValues: {
            animalCount: lactatingCount,
            weightKg,
            allocationByMonth: Array(12).fill(lactatingPct),
          },
        });
        classes.push({
          name: "Dry Cows",
          defaultStockClassName: "DAIRY_COW",
          initialValues: {
            animalCount: dryCount,
            weightKg,
            allocationByMonth: Array(12).fill(2.0),
          },
        });
      }
    } else if (type === "REPLACEMENT_HEIFER") {
      const pct = cattleDlwgToAllocation(dlwgKgDay);
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: "REPLACEMENT_HEIFER",
        initialValues: {
          animalCount,
          weightKg,
          allocationByMonth: Array(12).fill(pct),
        },
      });
    } else if (type === "BREEDING_COW") {
      const allocationByMonth =
        effectiveCalvingDate && weaningDate
          ? computeBreedingCowAllocations(effectiveCalvingDate, weaningDate)
          : undefined;
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: "BREEDING_COW",
        initialValues: { animalCount, weightKg, allocationByMonth },
      });
      // Auto-spawn growing cattle class
      if (weaningDate) {
        classes.push({
          name: "Growing Cattle",
          defaultStockClassName: "GROWING_CATTLE",
          initialValues: {
            animalCount: spawnedCount,
            weightKg: spawnedWeightKg,
            allocationByMonth: Array(12).fill(cattleDlwgToAllocation(0.5)),
            animalCountByMonth: computeSpawnedAnimalCounts(
              spawnedCount,
              weaningDate,
              planStartDateObj
            ),
          },
        });
      }
    } else if (type === "GROWING_CATTLE") {
      const pct = cattleDlwgToAllocation(dlwgKgDay);
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: "GROWING_CATTLE",
        initialValues: {
          animalCount,
          weightKg,
          allocationByMonth: Array(12).fill(pct),
          animalCountByMonth: computeAnimalCountsByMonth(
            animalCount,
            saleDate,
            planStartDateObj
          ),
        },
      });
    } else if (type === "BREEDING_EWE") {
      const allocationByMonth =
        effectiveTuppingDate && ewWeaningDate
          ? computeBreedingEweAllocations(
              effectiveTuppingDate,
              ewWeaningDate,
              scanningPct
            )
          : undefined;
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: "BREEDING_EWE",
        initialValues: { animalCount, weightKg, allocationByMonth },
      });
      // Auto-spawn growing lambs class
      if (ewWeaningDate) {
        classes.push({
          name: "Growing Lambs",
          defaultStockClassName: "GROWING_LAMB",
          initialValues: {
            animalCount: spawnedLambCount,
            weightKg: spawnedLambWeightKg,
            allocationByMonth: Array(12).fill(lambDlwgToAllocation(100)),
            animalCountByMonth: computeSpawnedAnimalCounts(
              spawnedLambCount,
              ewWeaningDate,
              planStartDateObj
            ),
          },
        });
      }
    } else if (type === "GROWING_LAMB") {
      const pct = lambDlwgToAllocation(dlwgGDay);
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: "GROWING_LAMB",
        initialValues: {
          animalCount,
          weightKg,
          allocationByMonth: Array(12).fill(pct),
          animalCountByMonth: computeAnimalCountsByMonth(
            animalCount,
            saleDate,
            planStartDateObj
          ),
        },
      });
    } else if (type === "BULL" || type === "RAM") {
      const pct =
        allocationLevel === "maintenance" ? 2.0 : type === "RAM" ? 3.0 : 2.5;
      classes.push({
        name: name || typeConfig.label,
        defaultStockClassName: type,
        initialValues: {
          animalCount,
          weightKg,
          allocationByMonth: Array(12).fill(pct),
        },
      });
    }

    onAdd(classes);
    onClose();
  }, [
    type,
    name,
    typeConfig,
    animalCount,
    weightKg,
    dairyCowSystem,
    calvingDate,
    dryOffDate,
    yieldLDay,
    lactatingCount,
    dryCount,
    ayrYieldLDay,
    dlwgKgDay,
    breedingCowStage,
    matingDate,
    breedingCalvingDate,
    effectiveCalvingDate,
    weaningDate,
    spawnedCount,
    spawnedWeightKg,
    breedingEweStage,
    tuppingDate,
    lambingDateDirect,
    effectiveTuppingDate,
    ewWeaningDate,
    scanningPct,
    spawnedLambCount,
    spawnedLambWeightKg,
    dlwgGDay,
    saleDate,
    allocationLevel,
    onAdd,
    onClose,
  ]);

  const isValid = (() => {
    if (type === "DAIRY_COW") {
      if (dairyCowSystem === "ayr") return true;
      return !!calvingDate && !!dryOffDate;
    }
    if (type === "BREEDING_COW")
      return breedingCowStage === "mating"
        ? !!matingDate
        : !!breedingCalvingDate;
    if (type === "BREEDING_EWE")
      return breedingEweStage === "lambing"
        ? !!lambingDateDirect
        : !!tuppingDate;
    return true;
  })();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Stock Class</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Type selector */}
            <TextField
              select
              label="Stock class type"
              value={type}
              onChange={(e) => setType(e.target.value as StockClassTypeId)}
              size="small"
              fullWidth
            >
              {STOCK_CLASS_TYPES.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              placeholder={typeConfig.label}
            />

            <Divider />

            {/* Common fields */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Number of animals"
                type="number"
                value={animalCount}
                onChange={(e) =>
                  setAnimalCount(Math.max(0, Number(e.target.value)))
                }
                size="small"
                fullWidth
                slotProps={{ htmlInput: { min: 0 } }}
              />
              <TextField
                label="Average liveweight (kg)"
                type="number"
                value={weightKg}
                onChange={(e) =>
                  setWeightKg(Math.max(1, Number(e.target.value)))
                }
                size="small"
                fullWidth
                slotProps={{ htmlInput: { min: 1, step: 5 } }}
              />
            </Stack>

            {/* === DAIRY COW === */}
            {type === "DAIRY_COW" && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.75 }}
                  >
                    Calving system
                  </Typography>
                  <ToggleButtonGroup
                    value={dairyCowSystem}
                    exclusive
                    onChange={(_, v: "block" | "ayr" | null) => {
                      if (v) setDairyCowSystem(v);
                    }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="block" sx={{ textTransform: "none" }}>
                      Block calving
                    </ToggleButton>
                    <ToggleButton value="ayr" sx={{ textTransform: "none" }}>
                      All-year-round
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {dairyCowSystem === "block" ? (
                  <>
                    <Stack direction="row" spacing={2}>
                      <DatePicker
                        label="Calving date"
                        value={calvingDate}
                        onChange={setCalvingDate}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                      <DatePicker
                        label="Dry-off date"
                        value={dryOffDate}
                        onChange={setDryOffDate}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            helperText:
                              "Lactation ends here — dry period uses 2.0% BW",
                          },
                        }}
                      />
                    </Stack>
                    <TextField
                      label="Target yield per cow (L/day)"
                      type="number"
                      value={yieldLDay}
                      onChange={(e) =>
                        setYieldLDay(Math.max(0, Number(e.target.value)))
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      helperText={`Lactation demand: ${(
                        ((weightKg * 0.02 + yieldLDay * 0.45) / weightKg) *
                        100
                      ).toFixed(1)}% BW · Dry period: 2.0% BW`}
                    />
                  </>
                ) : (
                  <>
                    <Box
                      sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        display="block"
                        sx={{ mb: 1 }}
                      >
                        Creates two stock classes: Lactating Cows and Dry Cows
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          label="Lactating cows"
                          type="number"
                          value={lactatingCount}
                          onChange={(e) =>
                            setLactatingCount(
                              Math.max(0, Number(e.target.value))
                            )
                          }
                          size="small"
                          fullWidth
                          slotProps={{ htmlInput: { min: 0 } }}
                          helperText="~85% of herd"
                        />
                        <TextField
                          label="Dry cows"
                          type="number"
                          value={dryCount}
                          onChange={(e) =>
                            setDryCount(Math.max(0, Number(e.target.value)))
                          }
                          size="small"
                          fullWidth
                          slotProps={{ htmlInput: { min: 0 } }}
                          helperText="~15% of herd · 2.0% BW"
                        />
                      </Stack>
                    </Box>
                    <TextField
                      label="Yield per lactating cow (L/day)"
                      type="number"
                      value={ayrYieldLDay}
                      onChange={(e) =>
                        setAyrYieldLDay(Math.max(0, Number(e.target.value)))
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      helperText={`Lactating cows: ${(
                        ((weightKg * 0.02 + ayrYieldLDay * 0.45) / weightKg) *
                        100
                      ).toFixed(1)}% BW · Dry cows: 2.0% BW (fixed)`}
                    />
                  </>
                )}
              </>
            )}

            {/* === REPLACEMENT HEIFER === */}
            {type === "REPLACEMENT_HEIFER" && (
              <>
                <Divider />
                <TextField
                  label="Target DLWG (kg/day)"
                  type="number"
                  value={dlwgKgDay}
                  onChange={(e) =>
                    setDlwgKgDay(Math.max(0, Number(e.target.value)))
                  }
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
                  helperText={`Allocation: ${cattleDlwgToAllocation(
                    dlwgKgDay
                  ).toFixed(
                    1
                  )}% BW  (≤0.5 kg/day = 2.0%, ≤1.0 = 2.5%, >1.0 = 3.0%)`}
                />
              </>
            )}

            {/* === BREEDING COW === */}
            {type === "BREEDING_COW" && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.75 }}
                  >
                    Current stage
                  </Typography>
                  <ToggleButtonGroup
                    value={breedingCowStage}
                    exclusive
                    onChange={(_, v: "mating" | "calving" | null) => {
                      if (v) {
                        setBreedingCowStage(v);
                        setMatingDate(null);
                        setBreedingCalvingDate(null);
                      }
                    }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="mating" sx={{ textTransform: "none" }}>
                      Mating / Pregnant
                    </ToggleButton>
                    <ToggleButton
                      value="calving"
                      sx={{ textTransform: "none" }}
                    >
                      Calved / Lactating
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {breedingCowStage === "mating" ? (
                  <DatePicker
                    label="Mating date"
                    value={matingDate}
                    onChange={setMatingDate}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                ) : (
                  <DatePicker
                    label="Calving date"
                    value={breedingCalvingDate}
                    onChange={setBreedingCalvingDate}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                )}
                {(effectiveCalvingDate || weaningDate) && (
                  <Typography variant="caption" color="text.secondary">
                    {breedingCowStage === "mating" && effectiveCalvingDate && (
                      <>
                        Expected calving (283 days):{" "}
                        <strong>
                          {effectiveCalvingDate.toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </strong>
                        {" · "}
                      </>
                    )}
                    {weaningDate && (
                      <>
                        Expected weaning (6 months):{" "}
                        <strong>
                          {weaningDate.toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </strong>
                      </>
                    )}
                  </Typography>
                )}
                {planEndDateObj && weaningDate && weaningDate > planEndDateObj && (
                  <Alert
                    severity="warning"
                    sx={{ fontSize: "0.78rem" }}
                    action={
                      onExtendPlan ? (
                        <Button
                          color="warning"
                          size="small"
                          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                          onClick={() =>
                            onExtendPlan(
                              format(endOfMonth(weaningDate), "yyyy-MM-dd")
                            )
                          }
                        >
                          Extend to{" "}
                          {weaningDate.toLocaleDateString("en-GB", {
                            month: "short",
                            year: "numeric",
                          })}
                        </Button>
                      ) : undefined
                    }
                  >
                    Expected weaning (
                    {weaningDate.toLocaleDateString("en-GB", {
                      month: "short",
                      year: "numeric",
                    })}
                    ) is after this plan ends. Growing Cattle will appear in the
                    extended plan year.
                  </Alert>
                )}
                <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Auto-added: Growing Cattle class (starts at weaning)
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Calves at weaning"
                      type="number"
                      value={spawnedCount}
                      onChange={(e) =>
                        setSpawnedCount(Math.max(0, Number(e.target.value)))
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0 } }}
                      helperText="Estimated from herd size"
                    />
                    <TextField
                      label="Weaning weight (kg)"
                      type="number"
                      value={spawnedWeightKg}
                      onChange={(e) =>
                        setSpawnedWeightKg(Math.max(1, Number(e.target.value)))
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 1, step: 5 } }}
                    />
                  </Stack>
                </Box>
              </>
            )}

            {/* === GROWING CATTLE === */}
            {type === "GROWING_CATTLE" && (
              <>
                <Divider />
                <TextField
                  label="Target DLWG (kg/day)"
                  type="number"
                  value={dlwgKgDay}
                  onChange={(e) =>
                    setDlwgKgDay(Math.max(0, Number(e.target.value)))
                  }
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
                  helperText={`Allocation: ${cattleDlwgToAllocation(
                    dlwgKgDay
                  ).toFixed(
                    1
                  )}% BW  (≤0.5 kg/day = 2.0%, ≤1.0 = 2.5%, >1.0 = 3.0%)`}
                />
                <DatePicker
                  label="Target sale date (optional)"
                  value={saleDate}
                  onChange={setSaleDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      helperText:
                        "Animal count will be set to 0 after this month",
                    },
                  }}
                />
              </>
            )}

            {/* === BREEDING EWE === */}
            {type === "BREEDING_EWE" && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.75 }}
                  >
                    Current stage
                  </Typography>
                  <ToggleButtonGroup
                    value={breedingEweStage}
                    exclusive
                    onChange={(_, v: "tupping" | "lambing" | null) => {
                      if (v) {
                        setBreedingEweStage(v);
                        setTuppingDate(null);
                        setLambingDateDirect(null);
                      }
                    }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton
                      value="tupping"
                      sx={{ textTransform: "none" }}
                    >
                      Tupped / Pregnant
                    </ToggleButton>
                    <ToggleButton
                      value="lambing"
                      sx={{ textTransform: "none" }}
                    >
                      Lambed / Lactating
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {breedingEweStage === "tupping" ? (
                  <DatePicker
                    label="Tupping date"
                    value={tuppingDate}
                    onChange={setTuppingDate}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                ) : (
                  <DatePicker
                    label="Lambing date"
                    value={lambingDateDirect}
                    onChange={setLambingDateDirect}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                )}
                {(effectiveLambingDate || ewWeaningDate) && (
                  <Typography variant="caption" color="text.secondary">
                    {breedingEweStage === "tupping" && effectiveLambingDate && (
                      <>
                        Expected lambing (147 days):{" "}
                        <strong>
                          {effectiveLambingDate.toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </strong>
                        {" · "}
                      </>
                    )}
                    {ewWeaningDate && (
                      <>
                        Expected weaning (90 days):{" "}
                        <strong>
                          {ewWeaningDate.toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </strong>
                      </>
                    )}
                  </Typography>
                )}
                <TextField
                  label="Scanning % (lambs per 100 ewes)"
                  type="number"
                  value={scanningPct}
                  onChange={(e) =>
                    setScanningPct(Math.max(100, Number(e.target.value)))
                  }
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { min: 100, step: 10 } }}
                  helperText="e.g. 160 = 1.6 lambs/ewe (60% twins, 40% singles)"
                />
                {planEndDateObj &&
                  ewWeaningDate &&
                  ewWeaningDate > planEndDateObj && (
                    <Alert
                      severity="warning"
                      sx={{ fontSize: "0.78rem" }}
                      action={
                        onExtendPlan ? (
                          <Button
                            color="warning"
                            size="small"
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                            onClick={() =>
                              onExtendPlan(
                                format(endOfMonth(ewWeaningDate), "yyyy-MM-dd")
                              )
                            }
                          >
                            Extend to{" "}
                            {ewWeaningDate.toLocaleDateString("en-GB", {
                              month: "short",
                              year: "numeric",
                            })}
                          </Button>
                        ) : undefined
                      }
                    >
                      Expected weaning (
                      {ewWeaningDate.toLocaleDateString("en-GB", {
                        month: "short",
                        year: "numeric",
                      })}
                      ) is after this plan ends. Growing Lambs will appear in
                      the extended plan year.
                    </Alert>
                  )}
                <Box
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Auto-added: Growing Lambs class (starts at weaning)
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Lambs at weaning"
                      type="number"
                      value={spawnedLambCount}
                      onChange={(e) =>
                        setSpawnedLambCount(Math.max(0, Number(e.target.value)))
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 0 } }}
                      helperText={`Estimated from ${animalCount} ewes × ${scanningPct}% scanning`}
                    />
                    <TextField
                      label="Weaning weight (kg)"
                      type="number"
                      value={spawnedLambWeightKg}
                      onChange={(e) =>
                        setSpawnedLambWeightKg(
                          Math.max(1, Number(e.target.value))
                        )
                      }
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    />
                  </Stack>
                </Box>
              </>
            )}

            {/* === GROWING LAMB === */}
            {type === "GROWING_LAMB" && (
              <>
                <Divider />
                <TextField
                  label="Target DLWG (g/day)"
                  type="number"
                  value={dlwgGDay}
                  onChange={(e) =>
                    setDlwgGDay(Math.max(0, Number(e.target.value)))
                  }
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 10 } }}
                  helperText={`Allocation: ${lambDlwgToAllocation(
                    dlwgGDay
                  ).toFixed(
                    1
                  )}% BW  (≤50 g/day = 2.0%, ≤150 = 2.5%, >150 = 3.0%)`}
                />
                <DatePicker
                  label="Target sale date (optional)"
                  value={saleDate}
                  onChange={setSaleDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      helperText:
                        "Animal count will be set to 0 after this month",
                    },
                  }}
                />
              </>
            )}

            {/* === BULL / RAM === */}
            {(type === "BULL" || type === "RAM") && (
              <>
                <Divider />
                <TextField
                  select
                  label="Allocation level"
                  value={allocationLevel}
                  onChange={(e) =>
                    setAllocationLevel(
                      e.target.value as "maintenance" | "active"
                    )
                  }
                  size="small"
                  fullWidth
                >
                  <MenuItem value="maintenance">
                    Maintenance only (2.0% BW)
                  </MenuItem>
                  <MenuItem value="active">
                    Active / mating season ({type === "RAM" ? "3.0" : "2.5"}%
                    BW)
                  </MenuItem>
                </TextField>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
